import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Handshake, Sparkles, Heart, Building2, GraduationCap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { arms } from "@/data/content";
import { IyafWordmark } from "@/components/brand/IyafMark";

const humanCircleImages = [
  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1599566150163-29194dcaad36?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
];

export default function Model() {
  const { t } = useTranslation();
  const effectChain = [
    { icon: Users, color: "#3663A8", from: t("modelDeep.chain.hubFrom"), to: t("modelDeep.chain.hubTo") },
    { icon: Handshake, color: "#8C8C8C", from: t("modelDeep.chain.consultFrom"), to: t("modelDeep.chain.consultTo") },
    { icon: GraduationCap, color: "#FF9F1C", from: t("modelDeep.chain.socialFrom"), to: t("modelDeep.chain.socialTo") },
    { icon: Heart, color: "#E01E84", from: t("modelDeep.chain.employersFrom"), to: t("modelDeep.chain.employersTo") },
  ];
  const scenarios = t("modelDeep.scenarios", { returnObjects: true }) || [];
  return (
    <div className="relative">
      {/* NEW HERO — Ecosystem Effect with human imagery */}
      <section className="relative pt-16 pb-24 overflow-hidden" data-testid="model-hero">
        <div className="container-iyaf relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="eyebrow text-iyaf-blue mb-5">{t("model.eyebrow")}</p>
              <h1 className="font-display font-black tracking-tighter leading-[0.95] text-balance text-5xl sm:text-6xl lg:text-7xl">
                {t("model.title")}
              </h1>
              <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-2xl">
                {t("model.lead")}
              </p>

              <div className="mt-10 space-y-3">
                {effectChain.map((step, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.45, delay: i * 0.08 }}
                    className="flex items-center gap-3 p-3 pr-5 rounded-full bg-white border border-black/5"
                    data-testid={`chain-step-${i}`}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${step.color}1A`, color: step.color }}>
                      <step.icon size={18} />
                    </div>
                    <div className="flex items-center gap-2 text-sm font-semibold flex-wrap">
                      <span className="text-iyaf-ink/85">{step.from}</span>
                      <ArrowRight size={14} className="text-iyaf-ink/40" />
                      <span style={{ color: step.color }}>{step.to}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right column — human composition with icons + photo circles */}
            <div className="lg:col-span-6">
              <div className="relative aspect-square max-w-lg mx-auto">
                {/* Soft background ring */}
                <motion.div
                  className="absolute inset-4 rounded-full border-2 border-dashed border-iyaf-blue/15"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 220, ease: "linear", repeat: Infinity }}
                />

                {/* 6 human face circles arranged around */}
                {humanCircleImages.map((src, i) => {
                  const angle = (i / humanCircleImages.length) * 360 - 90;
                  const r = 42;
                  const x = 50 + Math.cos((angle * Math.PI) / 180) * r;
                  const y = 50 + Math.sin((angle * Math.PI) / 180) * r;
                  const size = i % 2 === 0 ? 88 : 72;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.6 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.1 + i * 0.07 }}
                      className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden ring-4 ring-white shadow-lg"
                      style={{ left: `${x}%`, top: `${y}%`, width: size, height: size }}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </motion.div>
                  );
                })}

                {/* Center IYAF circle */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center shadow-2xl border border-black/5">
                  <div className="text-center">
                    <IyafWordmark variant="main" size="sm" />
                    <div className="flex justify-center gap-1.5 mt-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-iyaf-blue" />
                      <span className="w-1.5 h-1.5 rounded-full bg-iyaf-orange" />
                      <span className="w-1.5 h-1.5 rounded-full bg-iyaf-gray" />
                      <span className="w-1.5 h-1.5 rounded-full bg-iyaf-magenta" />
                    </div>
                  </div>
                </div>

                {/* Floating sparkle icons */}
                <Sparkles size={20} className="absolute top-[15%] right-[20%] text-iyaf-orange opacity-70 animate-pulse" />
                <Sparkles size={16} className="absolute bottom-[18%] left-[18%] text-iyaf-magenta opacity-60 animate-pulse" style={{ animationDelay: "1s" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Five scenarios */}
      <section className="py-24 bg-iyaf-paper" data-testid="model-scenarios">
        <div className="container-iyaf">
          <div className="max-w-2xl mb-16">
            <p className="eyebrow text-iyaf-blue mb-4">{t("modelDeep.fiveReal")}</p>
            <h2 className="h2 text-balance">{t("modelDeep.howMoves")}</h2>
            <p className="mt-4 font-editorial text-2xl text-iyaf-ink/70 leading-snug">
              {t("modelDeep.naturalResult")}
            </p>
          </div>

          <div className="space-y-6">
            {scenarios.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="p-8 sm:p-10 rounded-3xl border border-black/5 bg-white grid lg:grid-cols-12 gap-8 items-start"
                data-testid={`scenario-${i + 1}`}
              >
                <div className="lg:col-span-3">
                  <p className="font-display text-7xl font-black text-iyaf-blue/15 leading-none">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="font-display text-2xl font-black mt-3 leading-tight">{s.title}</h3>
                </div>
                <div className="lg:col-span-9">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {s.flow.map((step, j) => (
                      <span key={j} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-iyaf-paper border border-black/5 text-xs font-medium">
                        {step}
                        {j < s.flow.length - 1 && <ArrowRight size={11} className="text-iyaf-ink/40" />}
                      </span>
                    ))}
                  </div>
                  <p className="text-iyaf-ink/75 leading-relaxed">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Four arms quick links */}
      <section className="py-20">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-4">{t("modelDeep.armsConcertEyebrow")}</p>
          <h2 className="h2 mb-10">{t("modelDeep.armsConcertTitle")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {arms.map((arm, i) => (
              <motion.div
                key={arm.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Link to={arm.href} className="group block p-6 rounded-3xl bg-white border border-black/5 hover:border-transparent transition-all relative overflow-hidden" style={{ "--c": arm.color }} data-testid={`model-arm-${arm.key}`}>
                  <div className="absolute -right-12 -top-12 w-32 h-32 rounded-full opacity-[0.08] group-hover:opacity-100 transition" style={{ background: arm.color }} />
                  <div className="relative">
                    <IyafWordmark variant={arm.key} size="sm" />
                    <p className="small-caps text-[10px] mt-3" style={{ color: arm.color }}>{arm.status === "Active" ? t("armCards.active") : t("armCards.launching", { year: 2027 })}</p>
                    <p className="mt-2 text-sm text-iyaf-ink/65">{t(`armCards.${arm.key}.tagline`)}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: arm.color }}>
                      {t("modelDeep.explore")} <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
